<script lang="ts" setup>
import { ref, onMounted } from 'vue'
import BsTextButtonDialog from '@/legacy/components/TextButtonDialog.vue'
import { DialogType } from '@/types/DialogLegacy'
import useOverlay from '@/legacy/ts/useOverlay'

const props = defineProps({
	modelValue: {
		type: Boolean,
		default: false,
	},
	type: {
		type: String,
		default: DialogType.Normal,
	},
	title: {
		type: String,
		default: '',
	},
	text: {
		type: String,
		default: '',
	},
	cancel: {
		type: Function,
	},
	cancelText: {
		type: String,
		default: 'cancel',
	},
	confirm: {
		type: Function,
	},
	confirmText: {
		type: String,
		default: 'confirm',
	},
	closeText: {
		type: String,
	},
	isAutoClose: {
		type: Boolean,
		default: true,
	},
	isHideButtonCancel: {
		type: Boolean,
		default: false,
	},
	isHideButtonClose: {
		type: Boolean,
		default: true,
	},
	isLockScroll: {
		type: Boolean,
		default: false,
	},
	isSmallSize: {
		type: Boolean,
		default: false,
	},
})

const emit = defineEmits(['onOverlayClick', 'update:modelValue', 'on-hide'])

const isLoading = ref(true)
const { isShow, freezeBodyScroll } = useOverlay(props, updateModelValue, onHide)

function onHide() {
	emit('on-hide')
}

function updateModelValue(value: boolean) {
	emit('update:modelValue', value)
}

function onOverlayClick(target: any) {
	emit('onOverlayClick', target)
	if (props.isAutoClose) isShow.value = false
}

onMounted(() => {
	if (props.isLockScroll) {
		freezeBodyScroll()
	}

	isLoading.value = false
})
</script>

<template>
	<transition name="bs-dialog-action-zoom-in-out">
		<div
			class="fixed bottom-0 left-0 right-0 top-0 z-[500] flex items-center justify-center bg-[rgba(38,38,38,0.1)]"
			v-show="isShow"
		>
			<div
				:class="[
					'h-full w-full',
					type === DialogType.BottomSheet
						? 'bs-dialog-action__overlay__deep bg-[rgba(26,26,26,0.8)] duration-300 [transition-timing-function:cubic-bezier(0.25,0.8,0.25,1)]'
						: 'bg-[rgba(38,38,38,0.1)]',
				]"
				@click.capture="onOverlayClick"
			>
				<span
					v-if="!isHideButtonClose"
					class="absolute right-24px top-16px hidden cursor-pointer items-center lgOld:flex"
					@click="isShow = false"
				>
					<i
						class="inline-block h-[30px] w-[30px] bg-[length:100%_100%]"
						style="
							background-image: url('data:image/svg+xml;base64,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');
						"
					></i>
					<span
						class="ml-16px text-[16px] font-medium leading-[24px] tracking-[0.15px] text-white"
					>
						{{ closeText }}
					</span>
				</span>
			</div>
			<div
				id="dialogSurface"
				:class="[
					'bs-dialog-action__surface absolute bg-white shadow-[0_12px_17px_2px_rgba(0,0,0,0.14),0_5px_22px_4px_rgba(0,0,0,0.12),0_7px_8px_-4px_rgba(0,0,0,0.2)]',
					type === 'normal'
						? [
								'bs-dialog-action__normal left-1/2 top-[calc(60px+30%)] w-[calc(100%-32px)] max-w-[320px] -translate-x-1/2 rounded-[5px] p-16px',
								'lgOld:top-[calc((var(--vh)*100-216px)/3.5)] lgOld:max-w-[566px]',
								isSmallSize ? 'lgOld:max-w-[320px]' : '',
						  ]
						: '',
					type === 'bottom-sheet'
						? [
								'bs-dialog-action__bottom-sheet m-0 h-full w-full flex-[0_1_auto] translate-y-[100] self-end overflow-auto rounded-none p-0 duration-300 [transition-timing-function:cubic-bezier(0.25,0.8,0.25,1)]',
								'mdp:h-[calc(100vh-72px)] mdp:max-h-[calc(100vh-72px)] mdp:rounded-t-[15px] mdp:px-32px mdp:py-48px',
								'lgOld:h-[calc(100vh-66px)] lgOld:max-h-[calc(100vh-66px)] lgOld:px-24px lgOld:py-48px',
						  ]
						: '',
				]"
			>
				<slot name="content">
					<span
						v-if="title !== ''"
						class="mb-12px block text-[20px] font-semibold leading-[27px] tracking-[0.15px] text-[rgba(38,38,38,0.87)]"
					>
						{{ title }}
					</span>
					<span
						class="block min-h-[48px] text-[16px] font-medium leading-[24px] tracking-[0.15px] text-[rgba(38,38,38,0.87)]"
						v-html="text"
					></span>
					<div class="mt-16px flex items-center justify-end [&>*+*]:ml-16px">
						<BsTextButtonDialog
							:text="cancelText"
							:action="cancel"
							v-if="!isHideButtonCancel"
						/>
						<BsTextButtonDialog :text="confirmText" :action="confirm" />
					</div>
				</slot>
			</div>
		</div>
	</transition>
</template>
